// §7.1 / §11.7 — the app shortcut: a file that stands for an app (D-276).
//
// The desktop is `/Users/<user>/Desktop` and nothing else (D-276), so "an app icon on the desktop"
// has to be *a file*. This is that file. It is deliberately the smallest thing that can be one:
// a two-key JSON document naming an app id, with an extension the shell recognises.
//
// Why a real file rather than a list of pinned app ids in settings: the desktop then has exactly
// one notion of what is on it, Explorer shows the same icons the desktop does, grouping is `mkdir`
// plus `move`, renaming is `move`, and a file dropped from the host OS — which §7.7 has always
// written to `~/Desktop` — finally appears. A parallel pin-list would have made every one of those
// a second implementation that could disagree with the first.
//
// The parser is total: a shortcut whose bytes are damaged returns `null` rather than throwing, and
// the surface renders it as an ordinary file. A corrupt icon must never be able to take the
// desktop down with it.

/** The extension that makes a file a shortcut. Hangar's own — it collides with no real type. */
export const SHORTCUT_EXT = '.hlnk';

/** What a `.hlnk` holds. `v` is present so a later format can be told apart from this one. */
export interface AppShortcut {
  v: 1;
  app: string;
}

/** App ids are `^[a-z0-9][a-z0-9-]{1,31}$` (§9.2); built-ins use the same grammar. */
const APP_ID = /^[a-z0-9][a-z0-9-]{1,31}$/;

export function isShortcutName(name: string): boolean {
  return name.toLowerCase().endsWith(SHORTCUT_EXT);
}

/** The bytes of a shortcut to `appId`. Pretty-printed: it is a document a person may well open. */
export function serializeShortcut(appId: string): string {
  return `${JSON.stringify({ v: 1, app: appId } satisfies AppShortcut, null, 2)}\n`;
}

/**
 * The app a shortcut names, or `null` for anything that is not one.
 *
 * Every rejection here is a file that renders as a plain file instead — never an exception, and
 * never a shortcut pointing at a garbage id that the launcher would then be asked to open.
 */
export function parseShortcut(text: string): AppShortcut | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record['v'] !== 1) return null;
  const app = record['app'];
  if (typeof app !== 'string' || !APP_ID.test(app)) return null;
  return { v: 1, app };
}

/**
 * The filename a shortcut to an app called `name` gets: the app's display name, made safe for
 * §7.1's grammar, plus the extension. `/` is the only character a path segment truly cannot hold;
 * the rest are trimmed so a name never ends up empty or edge-padded.
 */
export function shortcutFileName(name: string): string {
  const safe = name.replace(/[/\\]/g, '-').replace(/\s+/g, ' ').trim().slice(0, 200);
  return `${safe.length > 0 ? safe : 'App'}${SHORTCUT_EXT}`;
}

/** The label an icon shows: the filename without the extension that is an implementation detail. */
export function shortcutLabel(fileName: string): string {
  return isShortcutName(fileName) ? fileName.slice(0, -SHORTCUT_EXT.length) : fileName;
}
